<i18n>
{
  "fr": {
    "configuratorFooter": {
      "title": "Récapitulatif",
      "error": "Vous devez renseigner tous les champs de personnalisation.",
      "action": {
        "goNext": {
          "addToCart": "Commander",
          "addToCartLoading": "En cours..."
        }
      }
    }
  }
}
</i18n>

<template>
  <div class="configurator-footer">
    <div class="configurator-footer__group configurator-footer__group--left">
      <ConfiguratorQuantitySelect name="configurator-quantity" required />
    </div>

    <span class="configurator-footer__separator"></span>

    <div class="configurator-footer__group configurator-footer__group--center">
      <ConfiguratorFooterPriceSummary
        :cup-price="pricePerUnit"
        :total-price="price"
        :total-price-without-vat="priceWithoutVAT"
        class="configurator-footer__price-summary"
      />
    </div>

    <div class="configurator-footer__group configurator-footer__group--right">
      <ConfiguratorProject class="configurator-footer__configurator-project" />

      <button
        type="button"
        :class="[
          'configurator-footer__action-next',
          'configurator-footer__action-next--add-to-cart',
          {
            'configurator-footer__action-next--loading':
              isAddToCartModalLoading,
          },
        ]"
        :disabled="!isCustomizationsComplete || isAddToCartModalLoading"
        @click="addToCart"
        v-text="
          isAddToCartModalLoading
            ? $t(`configuratorFooter.action.goNext.addToCartLoading`)
            : $t(`configuratorFooter.action.goNext.addToCart`)
        "
      ></button>

      <ConfiguratorAddToCartModal
        :is-modal-active="addToCartModalActive"
        @close="closeAddToCartModal"
        @show-cross-sell="openCrossSellModal"
      />

      <ConfiguratorCrossSellModal
        v-if="crossSellModalActive"
        :is-modal-active="crossSellModalActive"
        :product-id="activeProduct.id"
        :size-id="activeSize.id"
        :quantity-id="activeQuantity.id"
        :theme-type="themeType"
        :color="activeColor"
        :customizations="activeZoneCustomizations"
        @close="closeCrossSellModal"
      />
    </div>
  </div>
</template>

<script>
import ConfiguratorAddToCartModal from './ConfiguratorAddToCartModal';
import ConfiguratorCrossSellModal from './ConfiguratorCrossSellModal';
import {
  ConfiguratorGetters,
  mapGetters as mapConfiguratorGetter,
  mapState as mapConfiguratorState,
} from '../../store/modules/configurator';
import {
  LayoutMutation,
  LayoutView,
  mapState as mapLayoutState,
  prefixType as prefixLayoutType,
} from '../../store/modules/configurator/layout';
import ConfiguratorFooterPriceSummary from './ConfiguratorFooterPriceSummary';
import ConfiguratorQuantitySelect from './ConfiguratorQuantitySelect';
import { customizationsComplete } from '../../utils/ConfiguratorModuleUtils';
import ConfiguratorProject from './ConfiguratorProject.vue';
import { ThemeType } from '../../api/static/ThemeType';

export default {
  name: 'ConfiguratorFooter',
  components: {
    ConfiguratorFooterPriceSummary,
    ConfiguratorAddToCartModal,
    ConfiguratorCrossSellModal,
    ConfiguratorQuantitySelect,
    ConfiguratorProject,
  },
  data() {
    return {
      addToCartModalActive: false,
      isAddToCartModalLoading: false,
      crossSellModalActive: false,
      ThemeType,
    };
  },
  computed: {
    ...mapConfiguratorGetter({
      pricePerUnit: ConfiguratorGetters.GET_PRICE_PER_UNIT,
      price: ConfiguratorGetters.GET_PRICE,
      priceWithoutVAT: ConfiguratorGetters.GET_PRICE_WITHOUT_VAT,
      customizationZones:
        ConfiguratorGetters.GET_ZONE_CUSTOMIZATIONS_GROUPED_BY_GROUP,
      activeZoneCustomizations:
        ConfiguratorGetters.GET_ACTIVE_ZONE_CUSTOMIZATIONS,
      activeColor: ConfiguratorGetters.GET_ACTIVE_COLOR,
      activeProduct: ConfiguratorGetters.GET_ACTIVE_PRODUCT,
      activeSize: ConfiguratorGetters.GET_ACTIVE_SIZE,
      activeQuantity: ConfiguratorGetters.GET_ACTIVE_QUANTITY,
    }),
    ...mapConfiguratorState({
      themeType: 'themeType',
    }),
    ...mapLayoutState({
      activeStep: 'activeStep',
      activeView: 'activeView',
    }),
    isCustomizationsComplete() {
      // Force Vue to track nested properties by accessing them explicitly
      // eslint-disable-next-line no-unused-vars
      const _ = JSON.stringify(this.customizationZones);

      return (
        this.themeType === ThemeType.GENERIC ||
        customizationsComplete(this.customizationZones)
      );
    },
  },
  methods: {
    addToCart() {
      this.isAddToCartModalLoading = true;
      if (this.isCustomizationsComplete) {
        this.addToCartModalActive = true;
      } else {
        alert(this.$t('configuratorFooter.error'));
      }
      this.isAddToCartModalLoading = false;
    },
    closeAddToCartModal() {
      this.addToCartModalActive = false;
    },
    openCrossSellModal() {
      // Close AddToCart modal first
      this.addToCartModalActive = false;
      // Then open CrossSell modal
      this.$nextTick(() => {
        this.crossSellModalActive = true;
      });
    },
    closeCrossSellModal() {
      this.crossSellModalActive = false;
    },
    setActiveView(view) {
      if (this.activeView === LayoutView.CONTENT) {
        this.$store.commit(
          prefixLayoutType(LayoutMutation.SET_ACTIVE_VIEW),
          view
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-footer {
  $configurator-footer-breakpoint: $breakpoint-configurator-l;
  $configurator-footer-border-radius: 1.875rem; // 30px

  display: flex;
  font-size: 0.8125em;
  background: linear-gradient(
    120deg,
    $color-blue-cerulean-blue 44.9%,
    $color-arapawa 96.27%
  );

  box-shadow: 0 -0.25rem 0.25rem 0 rgba(0, 0, 0, 0.1);
  padding: 0.625rem 0.625rem 0.625rem 1.25rem;
  gap: 0.625rem;

  @include breakpoint($breakpoint-xs) {
    font-size: 1em;
  }

  @include breakpoint($configurator-footer-breakpoint) {
    padding: 0.625rem 0.625rem 0.625rem 1.875rem;
    border-radius: $configurator-footer-border-radius;
    box-shadow: none;
    justify-content: space-between;
  }

  &__separator {
    width: 1px;
    background-color: $color-gray-wild-sand;
    flex-shrink: 0;
  }

  &__group {
    display: flex;

    @include breakpoint($configurator-footer-breakpoint) {
      flex: 0 1 auto;
    }

    &--center {
      flex: 1;
      align-items: center;

      @include breakpoint($configurator-footer-breakpoint) {
        flex: none;
      }
    }

    &--right {
      gap: 0.625rem;
      align-items: center;
    }
  }

  &__group-item {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5em;
    text-align: center;
    flex: 0 1 100%;
  }

  &__configurator-project {
    display: none;

    @include breakpoint(1325px) {
      display: block;
      height: fit-content;
    }
  }

  &__action-next {
    border: 0;
    cursor: pointer;
    font-weight: 700;
    font-family: inherit;
    font-size: 1rem;
    margin: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    height: fit-content;
    background: $color-secondary;
    color: $color-white;
    flex: 1;
    min-width: 0;
    border-radius: $configurator-footer-border-radius;
    padding: 0.875rem 0.75rem 0.875rem 1rem;

    @include breakpoint($configurator-footer-breakpoint) {
      padding: 1.25rem 0.75rem;
    }

    &--default {
      text-align: right;

      &::after {
        @extend %icon;
        @extend %icon--arrow-right1;
        margin-left: 0.5em;
        transition: transform $default-transition-duration;
      }

      &:hover,
      &:focus {
        &::after {
          @include breakpoint($configurator-footer-breakpoint) {
            transform: translateX(0.5em);
          }
        }
      }
    }

    &--add-to-cart {
      text-align: left;
      text-transform: uppercase;

      &::after {
        @extend %icon;
        @extend %icon--arrow-right1;

        margin-left: 0.5em;
        transition: transform $default-transition-duration;
      }

      @media (hover: hover) {
        &:hover {
          opacity: 0.8;
        }

        &:hover,
        &:focus {
          &::before {
            @include breakpoint($configurator-footer-breakpoint) {
              transform: rotate(14deg);
            }
          }
        }
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  &__price-summary {
    max-width: 100%;

    @include breakpoint($configurator-footer-breakpoint) {
      flex: none;
    }
  }
}
</style>
